import React from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Modal, Select } from 'antd';
import debounce from 'lodash.debounce';
import { useUpdateTripTaskStatus } from 'api/trip-task/use-update-trip-task-status';
import { ETripTaskStatus, TRIP_TASK_STATUS_MAP } from 'types/trip-task-status';
import type { IStatusFormFields, ITripTaskStatusEditModalProps } from './types';

export const TripTaskStatusEditModal: React.FC<ITripTaskStatusEditModalProps> = (props) => {
  const { open, tripTask, onCancel, onSuccess } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm<IStatusFormFields>();

  const { mutateAsync: updateStatus, isLoading: isUpdatingStatus } = useUpdateTripTaskStatus(tripTask.tripId);

  const selectedStatus = Form.useWatch('status', form);
  const isOkDisabled = selectedStatus?.value === tripTask.status;

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleOk = debounce(() => form.submit(), 200);

  const onFinish = async (values: IStatusFormFields) => {
    await updateStatus({
      tripTaskId: tripTask.id,
      status: values.status.value,
    });

    form.resetFields();
    onSuccess();
  };

  const isDoneStatusDisabled =
    tripTask.status === ETripTaskStatus.unconfirmed || tripTask.status === ETripTaskStatus.todo;

  const statusOptions = [
    { value: ETripTaskStatus.executing, label: TRIP_TASK_STATUS_MAP[ETripTaskStatus.executing] },
    {
      value: ETripTaskStatus.done,
      label: TRIP_TASK_STATUS_MAP[ETripTaskStatus.done],
      disabled: isDoneStatusDisabled,
    },
  ];

  const initialValues = {
    status: { value: tripTask.status, label: TRIP_TASK_STATUS_MAP[tripTask.status] },
  };

  return (
    <Modal
      open={open}
      title={t('trip:update_trip_task_status')}
      width={400}
      confirmLoading={isUpdatingStatus}
      okButtonProps={{ disabled: isOkDisabled }}
      onCancel={handleCancel}
      onOk={handleOk}>
      <Form form={form} layout='vertical' style={{ marginTop: 16 }} initialValues={initialValues} onFinish={onFinish}>
        <Form.Item
          label={t('trip:trip_task_status')}
          name='status'
          rules={[{ required: true, message: t('trip:select_trip_task_status') }]}>
          <Select placeholder={t('trip:select_trip_task_status')} options={statusOptions} labelInValue />
        </Form.Item>
      </Form>
    </Modal>
  );
};
